import { createAxiosInstance } from "api/axiosConfig";
import {
  IB_RETURN_MAX_ATTEMPTS,
  IB_RETURN_RETRY_DELAY_MS,
  PENDING_IB_LAB_ORDER_KEY,
} from "features/labOrders/constants";

// The markers that signal a return from the Healthlink Integrated Browser, in the
// order we trust them:
//   1. sessionStorage — stashed before the redirect; the reliable source, because
//      Healthlink drops our redirectUrl params on the way back.
//   2. ?labOrderId — our own id, when it survives.
//   3. ?controlId — Healthlink's own message control number.
export const readIbReturnMarkers = (searchParams) => ({
  pendingLabOrderId: sessionStorage.getItem(PENDING_IB_LAB_ORDER_KEY),
  labOrderIdParam: searchParams?.get("labOrderId") ?? null,
  controlIdParam: searchParams?.get("controlId") ?? null,
});

export const hasPendingIbReturn = ({
  pendingLabOrderId,
  labOrderIdParam,
  controlIdParam,
}) => !!pendingLabOrderId || !!labOrderIdParam || !!controlIdParam;

// Resolve our own lab order id from the markers. A controlId is mapped through the
// patient's order list (newest first, so a just-created order is on the first
// page) by matching the message control id the submission returned.
export const resolveIbLabOrderId = async ({
  pendingLabOrderId,
  labOrderIdParam,
  controlIdParam,
  patientId,
  tenantVal,
}) => {
  if (pendingLabOrderId) return pendingLabOrderId;
  if (labOrderIdParam) return labOrderIdParam;
  if (!controlIdParam || !patientId || !tenantVal) return null;

  try {
    const axiosInstance = createAxiosInstance();
    const response = await axiosInstance.get(
      `${tenantVal}/healthlink/lab-orders/patient/${patientId}?page=1&rows=50&orderBy=DESC`
    );
    const match = (response?.data?.data ?? []).find(
      (order) => order.messageControlId === controlIdParam
    );

    return match?.id ?? null;
  } catch {
    return null;
  }
};

const wait = (milliseconds) =>
  new Promise((resolve) => setTimeout(resolve, milliseconds));

// Ask Healthlink for the completed order until it has one, or attempts run out.
// A false answer means "not ready yet", so it is retried rather than reported.
export const pollLabOrderDownload = async (downloadLabOrder, labOrderId) => {
  for (let attempt = 1; attempt <= IB_RETURN_MAX_ATTEMPTS; attempt++) {
    try {
      const result = await downloadLabOrder(labOrderId);

      if (result?.downloaded) return true;
    } catch (error) {
      console.error("Lab order download attempt failed", error);
    }

    if (attempt < IB_RETURN_MAX_ATTEMPTS) {
      await wait(IB_RETURN_RETRY_DELAY_MS);
    }
  }

  return false;
};
